"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function History() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const milestones = [{
    year: "2003",
    date: "3 / JANUARY",
    title: "TIALINX FOUNDED",
    description: "TiaLinx, Inc. established to pioneer advanced sensing architectures, focusing on the intersection of signal processing and high-fidelity\xA0 intelligence."
  }, {
    year: "2012",
    date: "05 / AUGUST",
    title: "FIRST MULTI-SENSOR DRONE\xA0",
    description: "Successfully deployed the inaugural sensor array, proving the viability of our proprietary signal-capture technology in terrestrial and sub-terrestrial environments."
  }, {
    year: "2019",
    date: "20 / OCTOBER",
    title: "INTELLIGENCE BACKBONE LAUNCH",
    description: "Providing real-time telemetry and predictive analytics for aerospace defense.\xA0"
  }, {
    year: "2025",
    date: "03 / MARCH",
    title: "DEEP SPACE SENSING PROTOCOL",
    description: "Development of the Deep Space Sensing Protocol (DSSP) has enabled unprecedented clarity in signal detection, setting a new industry standard for orbital awareness."
  }, {
    year: "2026",
    date: "CURRENT",
    title: "GLOBAL COMMAND INTEGRATION",
    description: "TiaLinx systems are being integrated into major global aerospace intelligence backbone for mission-critical operations."
  }];
  const presentMilestone = {
    year: "2025",
    date: "PRESENT",
    title: "ORBITAL COMMAND EXPANSION",
    description: "TiaLinx continues to address the next generation of autonomous orbital intelligence nodes, expanding our reach to deep-space monitoring and tactical signal analysis."
  };
  return <section data-section-id="4168" id="operational-history" className="bg-[#080F1A] text-[#B2BDC7] relative overflow-hidden py-24 md:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "radial-gradient(#B2BDC7 1px, transparent 1px)",
      backgroundSize: "40px 40px"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12">
        {}
        <div ref={headerRef} className={`mb-20 md:mb-28 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm uppercase tracking-[0.2em] text-[#507697] mb-6">CHRONOLOGY</p>
          <h2 className="font-default font-bold uppercase leading-[1.1] tracking-[-0.02em] text-[#B2BDC7]" style={{
          fontSize: "clamp(2.5rem, 5vw, 4.5rem)"
        }}>TWENTY YEARS<br />OF <span className="text-[#6697B9]" data-ast-id="f:/src/pages/About/sections/History.tsx|sig:29mcyn" data-ast-start="2870" data-ast-end="3030">INTELLIGENCE.</span></h2>
          <div className="mt-8 h-px w-24 bg-[#45596F]" />
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-[#45596F] md:-translate-x-1/2" aria-hidden="true">
            {}
            {Array.from({
            length: 24
          }).map((_, i) => <div key={i} data-index={i} className="absolute left-0 w-2 h-px bg-[#45596F] -translate-x-[3px]" style={{
            top: `${i * 100}px`
          }} />)}
          </div>

          {}
          <ol className="relative space-y-20 md:space-y-28">
            {milestones.map((milestone, i) => {
            const isLeft = i % 2 === 0;
            return <MilestoneItem key={milestone.year} data-index={i} milestone={milestone} isLeft={isLeft} />;
          })}

            {}
            <PresentMilestone milestone={presentMilestone} data-index={milestones.length} />

            {}
            <FuturePlaceholder data-index={milestones.length + 1} />
          </ol>
        </div>
      </div>
    </section>;
}
function MilestoneItem({
  milestone,
  isLeft,
  ...rest
}: {
  milestone: {
    year: string;
    date: string;
    title: string;
    description: string;
  };
  isLeft: boolean;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLLIElement>();
  return <li ref={ref} {...rest} className={`relative transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="absolute left-6 md:left-1/2 top-2 w-4 h-4 -translate-x-1/2 bg-[#6697B9]" aria-hidden="true" />

      <div className="grid md:grid-cols-2 md:gap-16">
        {}
        <div className={`pl-16 md:pl-0 ${isLeft ? "md:pr-12 md:text-right" : "md:order-2 md:pl-12"}`}>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#507697] mb-3">
            {milestone.date}
          </p>
          <h3 className="font-default font-bold uppercase leading-none tracking-tight mb-4 text-[#B2BDC7]" style={{
          fontSize: "clamp(3.5rem, 8vw, 6rem)"
        }}>
            {milestone.year}
          </h3>
        </div>

        {}
        <div className={`pl-16 md:pl-0 mt-2 md:mt-0 ${isLeft ? "" : "md:order-1 md:pr-12 md:text-right"}`}>
          <h4 className="font-default font-semibold uppercase text-xl md:text-2xl tracking-[0.05em] mb-4 text-[#B2BDC7]">
            {milestone.title}
          </h4>
          <p className="font-default text-base md:text-lg leading-relaxed text-[#B2BDC7]">
            {milestone.description}
          </p>
        </div>
      </div>
    </li>;
}
function PresentMilestone({
  milestone,
  ...rest
}: {
  milestone: {
    year: string;
    date: string;
    title: string;
    description: string;
  };
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLLIElement>();
  return <li ref={ref} {...rest} className={`relative transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="absolute left-6 md:left-1/2 top-2 w-6 h-6 -translate-x-1/2 bg-[#6697B9]" aria-hidden="true" />

      <div className="grid md:grid-cols-2 md:gap-16">
        <div className="pl-16 md:pl-0 md:pr-12 md:text-right">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#6697B9] mb-3">TBD</p>
          <h3 className="font-default font-bold uppercase leading-none tracking-tight mb-4 text-[#6697B9]" style={{
          fontSize: "clamp(3.5rem, 8vw, 6rem)"
        }}>2029</h3>
        </div>

        <div className="pl-16 md:pl-0 mt-2 md:mt-0">
          <h4 className="font-default font-semibold uppercase text-xl md:text-2xl tracking-[0.05em] mb-4 text-[#B2BDC7]">
            {milestone.title}
          </h4>
          <p className="font-default text-base md:text-lg leading-relaxed text-[#B2BDC7]">
            {milestone.description}
          </p>
        </div>
      </div>
    </li>;
}
function FuturePlaceholder({
  ...rest
}: {
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLLIElement>();
  return <li ref={ref} {...rest} className={`relative transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="absolute left-6 md:left-1/2 top-2 w-4 h-4 -translate-x-1/2 border border-[#45596F]" aria-hidden="true" />

      <div className="grid md:grid-cols-2 md:gap-16">
        <div className="pl-16 md:pl-0 md:pr-12 md:text-right">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#45596F] mb-3">
            &mdash;&mdash;
          </p>
          <h3 className="font-default font-bold uppercase leading-none tracking-tight mb-4 text-[#45596F]" style={{
          fontSize: "clamp(3.5rem, 8vw, 6rem)"
        }}>
            ----
          </h3>
        </div>

        <div className="pl-16 md:pl-0 mt-2 md:mt-0">
          <p className="font-default text-lg md:text-xl leading-relaxed text-[#45596F]">MISSION CONTINUITY — IN PROGRESS</p>
        </div>
      </div>
    </li>;
}